import { z } from 'zod';

const operations = [
  'prepareAttachment',
  'availability',
  'conversations',
  'conversation',
  'create',
  'reply',
  'unreadConversations',
  'markRead',
  'resolve',
] as const;
export type SupportOperation = (typeof operations)[number];
export function supportOperation(path: string): SupportOperation | undefined {
  const name = path.split('.').at(-1);
  return operations.find((operation) => operation === name);
}

export const traceIdSchema = z.uuid();
const errorCodes = z.enum([
  'CONFIGURATION_REQUIRED',
  'CONTACT_NOT_LINKED',
  'NOT_ELIGIBLE',
  'NOT_FOUND',
  'INVALID_INPUT',
  'UNAVAILABLE',
  'DELIVERY_UNKNOWN',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'BAD_REQUEST',
  'PRECONDITION_FAILED',
  'TIMEOUT',
  'BAD_GATEWAY',
  'INTERNAL_SERVER_ERROR',
  'SERVICE_UNAVAILABLE',
  'TOO_MANY_REQUESTS',
  'CLIENT_CLOSED_REQUEST',
]);
export function supportErrorCode(value: unknown): string {
  return errorCodes.safeParse(value).data ?? 'UNKNOWN';
}

type Summary = {
  outcome: 'completed' | 'failed' | 'skipped';
  result:
    | 'success'
    | 'partial'
    | 'failure'
    | 'unavailable'
    | 'delivery_unknown';
  errorCode?: string;
  delivery?: 'confirmed' | 'rejected' | 'unknown';
  enabled?: boolean;
  conversationCount?: number;
  messageCount?: number;
  hasNextPage?: boolean;
  partial?: boolean;
  historyTruncated?: boolean;
  setupComplete?: boolean;
  incompleteStepCount?: number;
};

// Parse only the metadata needed for metrics. Never return response content,
// subjects, patient IDs, attachment URLs, free-text errors or incomplete-step names.
const resultMetadata = z.object({
  upload: z.object({ attachmentId: z.string() }).nullish(),
  error: z.object({ code: z.unknown() }).nullish(),
  enabled: z.boolean().optional(),
  conversations: z.array(z.unknown()).optional(),
  nextCursor: z.string().nullish(),
  partial: z.boolean().optional(),
  conversation: z
    .object({
      messages: z.array(z.unknown()),
      historyTruncated: z.boolean(),
    })
    .nullish(),
  conversationId: z.string().nullish(),
  message: z.object({ id: z.string() }).nullish(),
  setupComplete: z.boolean().optional(),
  incompleteSteps: z.array(z.unknown()).optional(),
});

export function supportResult(
  operation: SupportOperation,
  value: unknown,
): Summary {
  const parsed = resultMetadata.safeParse(value);
  const writing = operation === 'create' || operation === 'reply';
  if (!parsed.success)
    return writing
      ? {
          outcome: 'failed',
          result: 'delivery_unknown',
          delivery: 'unknown',
          errorCode: 'INVALID_RESPONSE',
        }
      : {
          outcome: 'failed',
          result: 'failure',
          errorCode: 'INVALID_RESPONSE',
        };
  const data = parsed.data;
  const list =
    operation === 'conversations' || operation === 'unreadConversations';
  if (data.error) {
    const errorCode = supportErrorCode(data.error.code);
    const unknownDelivery =
      writing && (errorCode === 'DELIVERY_UNKNOWN' || errorCode === 'UNKNOWN');
    return {
      outcome: 'failed',
      result: unknownDelivery ? 'delivery_unknown' : 'failure',
      errorCode,
      ...(writing
        ? {
            delivery: unknownDelivery
              ? ('unknown' as const)
              : ('rejected' as const),
          }
        : {}),
    };
  }
  if (
    (operation === 'availability' && data.enabled === undefined) ||
    (list && (!data.conversations || data.partial === undefined))
  ) {
    return {
      outcome: 'failed',
      result: 'failure',
      errorCode: 'INVALID_RESPONSE',
    };
  }
  if (operation === 'prepareAttachment')
    return data.upload
      ? { outcome: 'completed', result: 'success' }
      : { outcome: 'failed', result: 'failure', errorCode: 'INVALID_RESPONSE' };
  if (operation === 'availability')
    return {
      outcome: data.enabled ? 'completed' : 'skipped',
      result: data.enabled ? 'success' : 'unavailable',
      enabled: data.enabled === true,
    };
  if (list)
    return {
      outcome: 'completed',
      result: data.partial ? 'partial' : 'success',
      conversationCount: data.conversations?.length ?? 0,
      hasNextPage: Boolean(data.nextCursor),
      partial: data.partial === true,
    };
  if (
    operation === 'conversation' ||
    operation === 'markRead' ||
    operation === 'resolve'
  )
    return data.conversation
      ? {
          outcome: 'completed',
          result: data.conversation.historyTruncated ? 'partial' : 'success',
          messageCount: data.conversation.messages.length,
          historyTruncated: data.conversation.historyTruncated,
        }
      : {
          outcome: 'failed',
          result: 'failure',
          errorCode: 'MISSING_CONVERSATION',
        };
  if (!data.conversationId || (operation === 'reply' && !data.message?.id))
    return {
      outcome: 'failed',
      result: 'delivery_unknown',
      delivery: 'unknown',
      errorCode: 'MISSING_ACKNOWLEDGEMENT',
    };
  if (operation === 'create')
    return {
      outcome: 'completed',
      result: data.setupComplete ? 'success' : 'partial',
      delivery: 'confirmed',
      setupComplete: data.setupComplete === true,
      incompleteStepCount: data.incompleteSteps?.length ?? 0,
    };
  return {
    outcome: 'completed',
    result: 'success',
    delivery: 'confirmed',
  };
}

export function supportFailure(
  operation: SupportOperation,
  code: unknown,
): Summary {
  const errorCode = supportErrorCode(code);
  const writing = operation === 'create' || operation === 'reply';
  const rejected = ['UNAUTHORIZED', 'FORBIDDEN', 'BAD_REQUEST'].includes(
    errorCode,
  );
  return {
    outcome: 'failed',
    result: writing && !rejected ? 'delivery_unknown' : 'failure',
    errorCode,
    ...(writing
      ? {
          delivery: rejected ? ('rejected' as const) : ('unknown' as const),
        }
      : {}),
  };
}
